import { Tab } from "@headlessui/react";
import { useAtom } from "jotai";
import {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { Button, ButtonLink } from "@/components/Interface/Button/Button";
import { CardContainer } from "@/components/Layout/BaseLayouts/CardContainer";
import { StepFlowNavigation } from "@/components/Layout/BaseLayouts/StepFlowLayout/StepFlowNavigation";
import { LocalLoadingSpinner } from "@/components/Layout/LoadingSpinner/LoadingSpinner";
import { RateCardGrid } from "@/components/Layout/Rates/RateCard/RateCardGrid";
import { RateTariffHint } from "@/components/Layout/Rates/RateCard/RateTariffHint";
import {
  getDataOnlyProducts,
  getMobileAbos,
  getSortedMobileAbosMonthly,
} from "@/components/Layout/Rates/rateSelection.utils";
import { RateTypeTabs } from "@/components/Layout/Rates/RateTypeTabs/RateTypeTabs";
import { useCustomerTribe } from "@/hooks/useCustomerTribe";
import { useInvalidBasketRecovery } from "@/hooks/useInvalidBasketRecovery";
import { usePreviousWizardPath } from "@/hooks/usePreviousWizardPath";
import { useQueryCallbacks } from "@/hooks/useQueryCallbacks";
import { DataContext } from "@/provider/DataContextProvider";
import {
  useAddOption,
  useGetDetailTribe,
  useListAvailableOptions,
  useSetDeviceByUuid,
} from "@/services/api";
import { accessTokenAtom, isAnonymousUserAtom } from "@/utils/atoms";
import { isInvalidBasketStatusError } from "@/utils/basketErrorUtils";
import { getEntry, hasBasketNumberPorting } from "@/utils/dataContextHelpers";
import { log } from "@/utils/log";
import { getDeviceForProduct } from "@/utils/productMatrix";
import {
  getPreselectedOption,
  isAnyFlatMobileTariff,
  resolveSelectedOptionForRateSelection,
  transformProducts,
  useHasPrechargedMobileAbo,
} from "@/utils/tariffUtils";
import {
  getStepAfterRate,
  getWizardRouteDecision,
  resolveWizardOptionId,
} from "@/utils/wizardNavigation";

export type RateFormProps = {
  rate: string; // The field's value must be string to enable setting the radio buttons by default
};

/**
 * The continue should only be disabled if:
 * - we don't have a rate selection yet
 * - no option for number porting (if available) was selected
 */
const getContinueIsDisabled = (
  currentRate: string,
  currentRateAsVoice: number | undefined,
) => currentRate === "" || (!currentRate && !currentRateAsVoice);

export const RateSelection: FC = () => {
  const { dataContext, setDataContext } = useContext(DataContext);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const previousPath = usePreviousWizardPath(2);

  const [rateTypeTabIndex, setRateTypeTabIndex] = useState(0);

  const entry = getEntry(dataContext);

  const isPurchaseIntent =
    dataContext.wizardEntry?.source === "purchase-intent";

  useEffect(() => {
    if (!isPurchaseIntent) {
      return;
    }

    const optionId = resolveWizardOptionId({
      basketOptionId: entry?.options?.[0]?.product?.id,
      selectedOptionId: dataContext.selectedOption?.id,
      wizardEntryOptionId: dataContext.wizardEntry?.optionId,
    });
    const { allowedMaxOnboardingStep, route } = getStepAfterRate(
      getWizardRouteDecision(optionId).includesNumberPorting,
    );

    setDataContext((prev) => ({ ...prev, allowedMaxOnboardingStep }));
    navigate(route, { replace: true });
  }, [
    dataContext.selectedOption?.id,
    dataContext.wizardEntry?.optionId,
    entry?.options,
    isPurchaseIntent,
    navigate,
    setDataContext,
  ]);

  const [isAnonymousUser] = useAtom(isAnonymousUserAtom);
  const [accessToken] = useAtom(accessTokenAtom);
  const { data: customerTribe } = useCustomerTribe({
    enabled: !isAnonymousUser,
  });
  const { data: tribeData } = useGetDetailTribe(customerTribe?.tribeId ?? "", {
    query: { enabled: !!accessToken && !!customerTribe?.tribeId },
  });
  const tribeDiscount = tribeData?.tribeDiscountNext;

  const rateFormMethods = useForm<RateFormProps, any, RateFormProps>({
    defaultValues: { rate: "" },
  });

  const { watch, reset } = rateFormMethods;

  const {
    openIfInvalidBasketError,
    invalidBasketModal,
    isInvalidBasketModalOpen,
  } = useInvalidBasketRecovery();

  useEffect(() => {
    getPreselectedOption(dataContext.basket)
      .then((preselectedOption) => {
        reset(preselectedOption);
      })
      .catch((error) => {
        openIfInvalidBasketError(error);
      });
    // Only re-run when the basket identity changes — the full basket object is replaced often and would re-fetch /options in a loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataContext.basket?.id]);

  const currentRate = watch("rate");

  const isMobileAbo = isAnyFlatMobileTariff(Number(currentRate));

  const hasPrechargedMobileAbo = useHasPrechargedMobileAbo(dataContext.basket);

  // Get all available options for this basket.
  // Use isLoading (no data yet), not isFetching — background refetches must not blank the UI.
  const listAvailableOptionsQuery = useListAvailableOptions(
    dataContext.basket?.id || -1,
    0,
    undefined,
    {
      query: {
        refetchOnWindowFocus: false,
        enabled: !!dataContext.basket?.id,
        retry: (failureCount, error) =>
          !isInvalidBasketStatusError(error) && failureCount < 3,
      },
    },
  );
  useQueryCallbacks(listAvailableOptionsQuery, {
    onSuccess: (data) => transformProducts(data),
  });
  const {
    data: availableOptions,
    isPending: isLoadingOptions,
    isError: isOptionsError,
    error: optionsError,
  } = listAvailableOptionsQuery;

  // Dead baskets cannot continue — show recovery dialog instead of cached rates.
  useEffect(() => {
    if (isOptionsError) {
      openIfInvalidBasketError(optionsError);
    }
  }, [isOptionsError, openIfInvalidBasketError, optionsError]);

  // Transform the available options into a usable data format (TransformedProduct)
  // and determine which product is preselected (precharged or recommended)
  const options = useMemo(
    () => availableOptions && transformProducts(availableOptions),
    [availableOptions],
  );
  const { processedProductsById, prechargeVoice } = options || {};

  const mobileAbos = useMemo(
    () => getMobileAbos(processedProductsById),
    [processedProductsById],
  );
  const dataOnlyProducts = useMemo(
    () => getDataOnlyProducts(processedProductsById),
    [processedProductsById],
  );

  const isONP = isMobileAbo || !!prechargeVoice;

  const handleNavigate = useCallback(
    (precharged: boolean) => {
      const { allowedMaxOnboardingStep, route } = getStepAfterRate(
        precharged ? hasPrechargedMobileAbo : isONP,
      );

      setDataContext((prev) => ({ ...prev, allowedMaxOnboardingStep }));
      navigate(route);
    },
    [hasPrechargedMobileAbo, isONP, navigate, setDataContext],
  );

  const prechargedProduct = availableOptions?.some(
    (product) => product.precharged,
  );

  // Used to correctly open the Data-only tab for precharged custom rates (e.g. Flat 10)
  const shouldShowCustomRatesTab =
    !!prechargedProduct &&
    !isAnyFlatMobileTariff(
      availableOptions?.find((option) => option.precharged === true)?.product
        ?.id,
    );

  // A number already committed to porting (e.g. the customer went back from
  // Checkout via "Change") can only land on a voice-capable mobile subscription — a
  // data-only tariff can't carry a ported number.
  const hasNumberPortingData = hasBasketNumberPorting(
    entry,
    dataContext.numberPorting,
  );

  // Tab 0 = Mobile Abos, Tab 1 = Data-only (billing period is chosen at checkout).
  // Controlled so a rate that loads after mount (back-nav / preselection) still opens the right tab.
  useEffect(() => {
    if (hasNumberPortingData) {
      setRateTypeTabIndex(0);
    } else if (shouldShowCustomRatesTab || (currentRate && !isMobileAbo)) {
      setRateTypeTabIndex(1);
    } else if (currentRate && isMobileAbo) {
      setRateTypeTabIndex(0);
    }
  }, [
    shouldShowCustomRatesTab,
    currentRate,
    isMobileAbo,
    hasNumberPortingData,
  ]);

  const { mutateAsync: addOption, isPending: isAddingOption } = useAddOption();
  const { mutateAsync: setDevice } = useSetDeviceByUuid();
  const prechargedAutoSkipStartedRef = useRef(false);

  // Reset when the basket entry changes (e.g. new ICCID / new basket).
  useEffect(() => {
    prechargedAutoSkipStartedRef.current = false;
  }, [dataContext.basket?.id, entry?.iccid, entry?.uuid]);

  useEffect(() => {
    // Precharged SIMs: ensure the tariff option is on the basket, then skip rate selection.
    // Flat Mobile → Number Porting (hasPrechargedMobileAbo + isONP).
    // Data tariffs (e.g. Flat 10) → personal-details / checkout.
    // Guard on isLoadingOptions: when switching SIM types (physical → eSIM), the basket
    // ID stays the same so React Query serves stale cached options that show precharged=true.
    if (
      isLoadingOptions ||
      !prechargedProduct ||
      isAddingOption ||
      !dataContext.basket?.id ||
      !entry?.uuid ||
      !processedProductsById
    ) {
      return;
    }

    const existingOptionId = entry.options?.[0]?.product?.id;

    if (existingOptionId) {
      if (!prechargedAutoSkipStartedRef.current) {
        prechargedAutoSkipStartedRef.current = true;
        handleNavigate(true);
      }

      return;
    }

    if (prechargedAutoSkipStartedRef.current) {
      return;
    }

    const prechargedOptionId = availableOptions?.find(
      (option) => option.precharged,
    )?.product?.id;

    if (!prechargedOptionId) {
      return;
    }

    const rateId = String(
      processedProductsById[prechargedOptionId]?.data ?? prechargedOptionId,
    );
    const selectedOption = resolveSelectedOptionForRateSelection({
      processedProductsById,
      currentRate: rateId,
      prechargeVoice: !!prechargeVoice,
    });

    if (!selectedOption?.id) {
      return;
    }

    prechargedAutoSkipStartedRef.current = true;

    addOption({
      basketId: dataContext.basket.id,
      uuid: entry.uuid,
      params: {
        optionId: selectedOption.id,
        quantity: 1,
        // Flat Mobile must set isONP so Number Porting is available on the entry.
        isONP: hasPrechargedMobileAbo,
      },
    })
      .then((data) => {
        if (!data.id || !entry.uuid || !selectedOption.id) {
          throw new Error(
            "Missing basket id, entry uuid, or option id after adding option",
          );
        }

        return setDevice({
          basketId: data.id,
          uuid: entry.uuid,
          deviceId: getDeviceForProduct(selectedOption.id).id,
        }).then((basketWithDevice) => ({ basketWithDevice, selectedOption }));
      })
      .then(({ basketWithDevice, selectedOption: option }) => {
        setDataContext((prev) => ({
          ...prev,
          basket: basketWithDevice,
          selectedOption: option,
        }));
        handleNavigate(true);
      })
      .catch((error) => {
        // Keep the guard set: isAddingOption is an effect dependency, so
        // resetting it here would immediately retry (and re-fail) the same
        // addOption call forever. A future attempt is allowed once the
        // basket entry actually changes (see the reset effect above).
        log(error);
      });
  }, [
    addOption,
    availableOptions,
    dataContext.basket?.id,
    entry?.esim,
    entry?.options,
    entry?.uuid,
    handleNavigate,
    hasPrechargedMobileAbo,
    isAddingOption,
    isLoadingOptions,
    prechargeVoice,
    prechargedProduct,
    processedProductsById,
    setDataContext,
    setDevice,
  ]);

  // Blank the step only while options are first loading or while the precharged
  // auto-skip addOption call is in flight — not for the whole time a precharged
  // flag is present (that left eSIM / failed-skip flows on an empty card).
  const isResolvingPrechargedPath =
    isLoadingOptions ||
    (isAddingOption && prechargedAutoSkipStartedRef.current);

  const backButton = (
    <ButtonLink
      variant="secondary"
      to={previousPath ?? "."}
      disabled={!previousPath}
    >
      {t("Common.label.back")}
    </ButtonLink>
  );

  if (isInvalidBasketModalOpen) {
    return (
      <div id="onboarding-rate-selection" className="flex justify-center py-16">
        {invalidBasketModal}
        <LocalLoadingSpinner />
      </div>
    );
  }

  if (isResolvingPrechargedPath) {
    return (
      <div id="onboarding-rate-selection" className="flex justify-center py-16">
        {invalidBasketModal}
        <LocalLoadingSpinner />
      </div>
    );
  }

  if (
    !processedProductsById ||
    Object.keys(processedProductsById).length === 0
  ) {
    return (
      <div
        id="onboarding-rate-selection"
        data-testid="onboarding-rate-selection"
        className="w-full"
      >
        {invalidBasketModal}
        <StepFlowNavigation primaryButton={null} secondaryButton={backButton} />
      </div>
    );
  }

  const currentRateAsVoice = processedProductsById[currentRate]?.voice;
  const selectedOption = resolveSelectedOptionForRateSelection({
    processedProductsById,
    currentRate,
    prechargeVoice: !!prechargeVoice,
  });

  const navigateToNext = () => {
    if (selectedOption?.id && dataContext.basket?.id && entry?.uuid) {
      const isPrecharged = !!selectedOption.precharged;

      addOption({
        basketId: dataContext.basket.id,
        uuid: entry.uuid,
        params: {
          optionId: selectedOption.id,
          quantity: 1,
          isONP: isPrecharged ? hasPrechargedMobileAbo : isONP,
        },
      }).then((data) => {
        setDataContext({
          ...dataContext,
          basket: data,
          selectedOption,
        });
        handleNavigate(isPrecharged);
      });
    } else {
      log(
        `option "${selectedOption?.id}", ${dataContext.basket?.id} "basket id", "entry 0 uuid" ${entry?.uuid}`,
      );
    }
  };

  const continueIsDisabled = getContinueIsDisabled(
    currentRate,
    currentRateAsVoice,
  );

  const disableRateTypeSwitch = !!prechargedProduct;

  const isLoading = isAddingOption || rateFormMethods.formState.isLoading;

  const setRateFromMobileAbo = (rate: string) =>
    rateFormMethods.setValue("rate", rate);

  return (
    <div
      data-testid="onboarding-rate-selection"
      id="onboarding-rate-selection"
      className="flex w-full flex-col gap-6"
    >
      {invalidBasketModal}
      <FormProvider {...rateFormMethods}>
        <CardContainer
          className="flex w-full flex-col rounded-lg pb-2"
          variant="white"
        >
          <RateTypeTabs
            selectedIndex={rateTypeTabIndex}
            onIndexChange={setRateTypeTabIndex}
            listClassName="px-4 pb-2 pt-4"
            disabled={disableRateTypeSwitch}
            hideSecondTab={hasNumberPortingData}
            firstLabel={t("Onboarding.sections.rate-selection.tabs.mobileAbos")}
            secondLabel={t("Onboarding.sections.rate-selection.tabs.dataOnly")}
          >
            <Tab.Panels className="flex flex-col gap-6 px-4 py-2">
              <Tab.Panel>
                <RateCardGrid
                  ariaLabel={t(
                    "Onboarding.sections.rate-selection.tabs.mobileAbos",
                  )}
                  value={rateFormMethods.watch("rate")}
                  products={getSortedMobileAbosMonthly(mobileAbos)}
                  tribeDiscount={tribeDiscount}
                  hint={<RateTariffHint />}
                  onChange={setRateFromMobileAbo}
                />
              </Tab.Panel>
              <Tab.Panel>
                <RateCardGrid
                  ariaLabel={t(
                    "Onboarding.sections.rate-selection.tabs.dataOnly",
                  )}
                  value={rateFormMethods.watch("rate")}
                  products={dataOnlyProducts}
                  hint={<RateTariffHint />}
                  onChange={setRateFromMobileAbo}
                />
              </Tab.Panel>
            </Tab.Panels>
          </RateTypeTabs>
        </CardContainer>
      </FormProvider>

      <StepFlowNavigation
        primaryButton={
          <Button
            dataTestid="continue-button"
            variant="primary"
            disabled={continueIsDisabled}
            loading={isLoading}
            onClick={() => navigateToNext()}
          >
            {t("Common.label.forward")}
          </Button>
        }
        secondaryButton={backButton}
      />
    </div>
  );
};
